import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  authService,
  type CurrentUser,
  type LoginCredentials,
  type RegisterData,
} from '../services/auth.service';
import { removeAuthToken } from '../services/api.service';
import { isOidcEnabled, getOidcLoginUrl, getOidcLogoutUrl, AUTH_MODE } from '../config/oidc';

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  authMode: 'local' | 'oidc' | 'hybrid';
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithOidc: () => void;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  handleOidcCallback: (accessToken: string, refreshToken: string) => Promise<void>;
}


const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * AuthProvider - handles both local and OIDC authentication
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Check for existing session on mount
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const currentUser = await authService.getCurrentUser();
          if (isMounted) {
            setUser(currentUser);
          }
        } catch {
          authService.removeToken();
        }
      }
      if (isMounted) {
        setIsLoading(false);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    await authService.login(credentials);
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  const loginWithOidc = useCallback(() => {
    if (!isOidcEnabled()) {
      throw new Error('OIDC not enabled');
    }
    // Redirect to backend OIDC login endpoint
    window.location.href = getOidcLoginUrl();
  }, []);

  const handleOidcCallback = useCallback(async (accessToken: string, refreshToken: string) => {
    // Store tokens
    authService.setToken(accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    
    // Fetch user info
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    await authService.register(data);
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  const logout = useCallback(async () => {
    // In OIDC/Entra mode, redirect to Authentik logout for Single Logout (SLO)
    // This logs out of ALL connected applications
    if (isOidcEnabled()) {
      // Get logout URL BEFORE clearing token (it includes the token for id_token_hint)
      const logoutUrl = getOidcLogoutUrl();
      
      // Redirect to Authentik FIRST, before clearing anything
      // This ensures the redirect happens before any React re-renders
      window.location.href = logoutUrl;
      return;
    }
    
    // For local auth mode:
    // Clear local token
    removeAuthToken();
    
    // Clear React state
    setUser(null);
    queryClient.clear();
    
    // Try to call server logout (but don't wait for it to complete)
    authService.logout().catch(() => {
      // Ignore errors - token is already cleared locally
    });
    
    // Redirect to login page
    window.location.href = '/login';
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    if (authService.isAuthenticated()) {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    authMode: AUTH_MODE as 'local' | 'oidc' | 'hybrid',
    login,
    loginWithOidc,
    register,
    logout,
    refreshUser,
    handleOidcCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
